import { z } from 'zod'

import type {
  Promotion,
  PromotionChannel,
  PromotionLifecycle,
  PromotionListItem,
  PromotionPayload,
  PromotionScope,
  PromotionType,
} from '@/app/services/promotion.service'

const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/

function parseLocalDate(value: string): Date {
  const [year, month, day] = value.split('-').map(Number)
  return new Date(year, month - 1, day)
}

function isValidDateOnly(value: string): boolean {
  if (!dateOnlyPattern.test(value)) return false

  const [year, month, day] = value.split('-').map(Number)
  const date = parseLocalDate(value)

  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  )
}

function toLocalDateValue(value: string, inclusiveEnd = false): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  if (inclusiveEnd) date.setMilliseconds(date.getMilliseconds() - 1)

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function toPromotionStartDateValue(value: string): string {
  return toLocalDateValue(value)
}

export function toPromotionEndDateValue(value: string): string {
  return toLocalDateValue(value, true)
}

export function toPromotionStartIso(value: string): string {
  return parseLocalDate(value).toISOString()
}

export function toPromotionEndIso(value: string): string {
  const date = parseLocalDate(value)
  date.setDate(date.getDate() + 1)
  return date.toISOString()
}

export const promotionTypeLabels: Record<PromotionType, string> = {
  percentage: 'Porcentaje',
  fixed_amount: 'Importe fijo',
  buy_x_pay_y: 'Compra X y paga Y',
}

export const promotionChannelLabels: Record<PromotionChannel, string> = {
  pos: 'POS',
  ecommerce: 'Ecommerce',
  both: 'Ambos',
}

export const promotionScopeLabels: Record<PromotionScope, string> = {
  global: 'Global',
  branches: 'Sucursales seleccionadas',
}

export const promotionLifecycleLabels: Record<PromotionLifecycle, string> = {
  scheduled: 'Programada',
  active: 'Activa',
  expired: 'Expirada',
  inactive: 'Inactiva',
}

export const promotionFormSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, 'El nombre es obligatorio')
      .max(255, 'El nombre no puede superar los 255 caracteres'),
    description: z.string(),
    type: z.enum(['percentage', 'fixed_amount', 'buy_x_pay_y']),
    channel: z.enum(['pos', 'ecommerce', 'both']),
    scope_type: z.enum(['global', 'branches']),
    value: z.string(),
    fixed_application: z.enum(['', 'per_unit', 'per_line']),
    buy_quantity: z.string(),
    pay_quantity: z.string(),
    starts_at: z
      .string()
      .refine(isValidDateOnly, 'La fecha de inicio es obligatoria'),
    ends_at: z
      .string()
      .refine(isValidDateOnly, 'La fecha de fin es obligatoria'),
    is_active: z.boolean(),
    product_ids: z.array(z.number().int().positive()),
    excluded_product_ids: z.array(z.number().int().positive()),
    category_ids: z.array(z.number().int().positive()),
    department_ids: z.array(z.number().int().positive()),
    sucursal_ids: z.array(z.number().int().positive()),
  })
  .superRefine((values, context) => {
    if (
      values.product_ids.length === 0 &&
      values.category_ids.length === 0 &&
      values.department_ids.length === 0
    ) {
      context.addIssue({
        code: 'custom',
        path: ['product_ids'],
        message: 'Selecciona al menos un producto, categoría o departamento',
      })
    }

    if (
      values.product_ids.some((id) => values.excluded_product_ids.includes(id))
    ) {
      context.addIssue({
        code: 'custom',
        path: ['excluded_product_ids'],
        message: 'Un producto no puede estar incluido y excluido al mismo tiempo',
      })
    }

    if (values.scope_type === 'global' && values.sucursal_ids.length > 0) {
      context.addIssue({
        code: 'custom',
        path: ['sucursal_ids'],
        message: 'Una promoción global no puede incluir sucursales',
      })
    }

    if (values.scope_type === 'branches' && values.sucursal_ids.length === 0) {
      context.addIssue({
        code: 'custom',
        path: ['sucursal_ids'],
        message: 'Selecciona al menos una sucursal',
      })
    }

    if (
      isValidDateOnly(values.starts_at) &&
      isValidDateOnly(values.ends_at) &&
      values.ends_at < values.starts_at
    ) {
      context.addIssue({
        code: 'custom',
        path: ['ends_at'],
        message: 'La fecha de fin no puede ser anterior al inicio',
      })
    }

    const value = Number(values.value)
    if (
      values.type === 'percentage' &&
      (!Number.isFinite(value) || value <= 0 || value > 100)
    ) {
      context.addIssue({
        code: 'custom',
        path: ['value'],
        message: 'El porcentaje debe ser mayor que 0 y menor o igual a 100',
      })
    }

    if (values.type === 'fixed_amount') {
      if (!Number.isFinite(value) || value <= 0) {
        context.addIssue({
          code: 'custom',
          path: ['value'],
          message: 'El importe fijo debe ser mayor que 0',
        })
      }

      if (!values.fixed_application) {
        context.addIssue({
          code: 'custom',
          path: ['fixed_application'],
          message: 'Selecciona cómo se aplicará el importe fijo',
        })
      }
    }

    if (values.type === 'buy_x_pay_y') {
      const buyQuantity = Number(values.buy_quantity)
      const payQuantity = Number(values.pay_quantity)

      if (!Number.isInteger(buyQuantity) || buyQuantity < 2) {
        context.addIssue({
          code: 'custom',
          path: ['buy_quantity'],
          message: 'La cantidad a comprar debe ser un entero de al menos 2',
        })
      }

      if (!Number.isInteger(payQuantity) || payQuantity < 1) {
        context.addIssue({
          code: 'custom',
          path: ['pay_quantity'],
          message: 'La cantidad a pagar debe ser un entero de al menos 1',
        })
      } else if (Number.isInteger(buyQuantity) && payQuantity >= buyQuantity) {
        context.addIssue({
          code: 'custom',
          path: ['pay_quantity'],
          message:
            'La cantidad a pagar debe ser menor que la cantidad a comprar',
        })
      }
    }
  })

export type PromotionFormValues = z.infer<typeof promotionFormSchema>

export const emptyPromotionForm: PromotionFormValues = {
  name: '',
  description: '',
  type: 'percentage',
  channel: 'both',
  scope_type: 'global',
  value: '',
  fixed_application: '',
  buy_quantity: '',
  pay_quantity: '',
  starts_at: '',
  ends_at: '',
  is_active: true,
  product_ids: [],
  excluded_product_ids: [],
  category_ids: [],
  department_ids: [],
  sucursal_ids: [],
}

export function promotionToFormValues(
  promotion: Promotion,
): PromotionFormValues {
  return {
    name: promotion.name,
    description: promotion.description ?? '',
    type: promotion.type,
    channel: promotion.channel,
    scope_type: promotion.scope_type,
    value: promotion.value ?? '',
    fixed_application: promotion.fixed_application ?? '',
    buy_quantity: promotion.buy_quantity?.toString() ?? '',
    pay_quantity: promotion.pay_quantity?.toString() ?? '',
    starts_at: toPromotionStartDateValue(promotion.starts_at),
    ends_at: toPromotionEndDateValue(promotion.ends_at),
    is_active: promotion.is_active,
    product_ids: promotion.products.map((product) => product.id),
    excluded_product_ids: promotion.excluded_products.map(
      (product) => product.id,
    ),
    category_ids: promotion.categories.map((category) => category.id),
    department_ids: promotion.departments.map((department) => department.id),
    sucursal_ids: promotion.sucursals.map((sucursal) => sucursal.id),
  }
}

export const promotionToForm = promotionToFormValues

export function promotionFormToPayload(
  values: PromotionFormValues,
): PromotionPayload {
  return {
    name: values.name.trim(),
    description: values.description.trim() || null,
    type: values.type,
    channel: values.channel,
    scope_type: values.scope_type,
    value:
      values.type === 'buy_x_pay_y'
        ? null
        : Number(values.value).toFixed(2),
    fixed_application:
      values.type === 'fixed_amount' ? values.fixed_application || null : null,
    buy_quantity:
      values.type === 'buy_x_pay_y' ? Number(values.buy_quantity) : null,
    pay_quantity:
      values.type === 'buy_x_pay_y' ? Number(values.pay_quantity) : null,
    starts_at: toPromotionStartIso(values.starts_at),
    ends_at: toPromotionEndIso(values.ends_at),
    is_active: values.is_active,
    product_ids: [...new Set(values.product_ids)],
    excluded_product_ids: [...new Set(values.excluded_product_ids)],
    category_ids: [...new Set(values.category_ids)],
    department_ids: [...new Set(values.department_ids)],
    sucursal_ids:
      values.scope_type === 'global' ? [] : [...new Set(values.sucursal_ids)],
  }
}

const currencyFormatter = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
})

export function formatPromotionRule(
  promotion: Promotion | PromotionListItem,
): string {
  if (promotion.type === 'percentage') {
    return `${Number(promotion.value ?? 0).toLocaleString('es-MX')}% de descuento`
  }

  if (promotion.type === 'fixed_amount') {
    const application =
      promotion.fixed_application === 'per_unit' ? 'por unidad' : 'por línea'
    return `${currencyFormatter.format(Number(promotion.value ?? 0))} ${application}`
  }

  return `Compra ${promotion.buy_quantity ?? 0}, paga ${promotion.pay_quantity ?? 0}`
}
